import { useEffect, useState } from "react";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * SaveButton — one-tap bookmark for any Library chapter.
 * Persists to `library_saves` via /api/library/{slug}/save.
 *
 * Props:
 *   slug        — the library article slug (required)
 *   title       — optional, used in the toast for a warmer confirmation
 *   size        — "sm" (default, 3.5w icon) | "md" (4w icon)
 *   initialSaved — pre-hydrate to avoid a flicker when a parent already knows
 *   onChange    — optional callback (savedBool, totalNumber) after each toggle
 *   testId      — data-testid override
 *   className   — extra classes on the wrapper button
 *   stopEvent   — when true, stops the click bubbling (useful when the button
 *                 sits inside a wrapping <Link>). Default true.
 */
export default function SaveButton({
  slug,
  title,
  size = "sm",
  initialSaved,
  onChange,
  testId,
  className = "",
  stopEvent = true,
}) {
  const { user } = useAuth();
  const [saved, setSaved] = useState(Boolean(initialSaved));
  const [busy, setBusy] = useState(false);

  // If parent didn't hydrate, check the light /slugs endpoint once
  useEffect(() => {
    if (initialSaved !== undefined || !user || !slug) return;
    let cancelled = false;
    api.get("/library/saved/me/slugs")
      .then(({ data }) => {
        if (!cancelled && Array.isArray(data.slugs)) {
          setSaved(data.slugs.includes(slug));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user, slug, initialSaved]);

  const toggle = async (e) => {
    if (stopEvent) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!user) {
      toast.info("Sign in to save chapters for later.");
      return;
    }
    if (busy) return;
    setBusy(true);
    const wasSaved = saved;
    setSaved(!wasSaved);   // optimistic
    try {
      const { data } = wasSaved
        ? await api.delete(`/library/${slug}/save`)
        : await api.post(`/library/${slug}/save`);
      setSaved(Boolean(data.saved));
      if (onChange) onChange(Boolean(data.saved), data.total);
      if (!wasSaved) {
        toast.success(title ? `Saved "${title.slice(0, 60)}"` : "Saved to your notebook");
      } else {
        toast.message("Removed from your notebook");
      }
    } catch {
      setSaved(wasSaved); // revert
      toast.error("Could not update your notebook — try again.");
    } finally {
      setBusy(false);
    }
  };

  const iconClass = size === "md" ? "w-4 h-4" : "w-3.5 h-3.5";
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      className={`inline-flex items-center justify-center rounded-full transition-colors ${saved ? "text-gold hover:text-marigold" : "text-royal/40 hover:text-royal"} ${busy ? "opacity-50" : ""} ${className}`}
      aria-label={saved ? "Remove from notebook" : "Save to notebook"}
      aria-pressed={saved}
      title={saved ? "Saved · tap to remove" : "Save for later"}
      data-testid={testId || `save-btn-${slug}`}
    >
      {saved
        ? <BookmarkCheck className={`${iconClass} fill-gold`} />
        : <Bookmark className={iconClass} />}
    </button>
  );
}
